let activeOverlay = null;
const TAP_MOVEMENT_THRESHOLD = 8;
let removeOutsideInteractionListener = null;

function lockPageScroll() {
  document.documentElement.classList.add('product-gallery-zoom-locked');
  document.body.classList.add('product-gallery-zoom-locked');
}

function unlockPageScroll() {
  document.documentElement.classList.remove('product-gallery-zoom-locked');
  document.body.classList.remove('product-gallery-zoom-locked');
}

function getPointerPosition(event, fallbackRect) {
  if (event?.changedTouches?.length) {
    return {
      clientX: event.changedTouches[0].clientX,
      clientY: event.changedTouches[0].clientY,
    };
  }

  if (event?.touches?.length) {
    return {
      clientX: event.touches[0].clientX,
      clientY: event.touches[0].clientY,
    };
  }

  if (typeof event?.clientX === 'number' && typeof event?.clientY === 'number') {
    return {
      clientX: event.clientX,
      clientY: event.clientY,
    };
  }

  return {
    clientX: fallbackRect.left + fallbackRect.width / 2,
    clientY: fallbackRect.top + fallbackRect.height / 2,
  };
}

function toggleLoadingSpinner(image, isVisible) {
  const loadingSpinner = image.parentElement.parentElement.querySelector('.loading__spinner');
  if (!loadingSpinner) return;

  loadingSpinner.classList.toggle('hidden', !isVisible);
}

function closeActiveOverlay() {
  if (!activeOverlay) return;

  activeOverlay.opener?.classList.remove('is-zoom-active');
  activeOverlay.image.style.opacity = '100%';
  if (removeOutsideInteractionListener) {
    removeOutsideInteractionListener();
    removeOutsideInteractionListener = null;
  }
  unlockPageScroll();
  activeOverlay.remove();
  activeOverlay = null;
}

function createOverlay(image, opener, zoomRatio) {
  const overlay = document.createElement('div');
  const source = image.currentSrc || image.src;

  overlay.setAttribute('class', 'image-magnify-full-size');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.backgroundImage = `url('${source}')`;
  overlay.style.backgroundColor = 'var(--gradient-background)';
  overlay.style.backgroundRepeat = 'no-repeat';
  overlay.style.backgroundSize = `${image.naturalWidth * zoomRatio}px`;
  overlay.style.zIndex = '2';
  overlay.image = image;
  overlay.opener = opener;

  return overlay;
}

function moveOverlay(image, overlay, event, zoomRatio) {
  const rect = image.getBoundingClientRect();
  const pointer = getPointerPosition(event, rect);
  const x = Math.min(Math.max(pointer.clientX - rect.left, 0), rect.width);
  const y = Math.min(Math.max(pointer.clientY - rect.top, 0), rect.height);
  const xPercent = `${(x / rect.width) * 100}%`;
  const yPercent = `${(y / rect.height) * 100}%`;

  overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
  overlay.style.backgroundSize = `${image.naturalWidth * zoomRatio}px`;
}

function openMagnify(image, opener, event, zoomRatio) {
  closeActiveOverlay();

  const overlay = createOverlay(image, opener, zoomRatio);
  const sourceLoader = new Image();
  sourceLoader.src = image.currentSrc || image.src;

  image.style.opacity = '50%';
  opener?.classList.add('is-zoom-active');
  toggleLoadingSpinner(image, true);
  if (window.matchMedia('(max-width: 749px)').matches) {
    lockPageScroll();
  }

  sourceLoader.onload = () => {
    toggleLoadingSpinner(image, false);
    image.parentElement.insertBefore(overlay, image);
    image.style.opacity = '100%';
    activeOverlay = overlay;
    moveOverlay(image, overlay, event, zoomRatio);

    const handleOutsideInteraction = (interactionEvent) => {
      if (!activeOverlay || !activeOverlay.opener) return;
      if (activeOverlay.opener.contains(interactionEvent.target)) return;
      closeActiveOverlay();
    };

    document.addEventListener('pointerdown', handleOutsideInteraction, true);
    removeOutsideInteractionListener = () => {
      document.removeEventListener('pointerdown', handleOutsideInteraction, true);
    };

    let interactionStart = null;

    const startInteraction = (interactionEvent) => {
      interactionStart = getPointerPosition(interactionEvent, overlay.getBoundingClientRect());
    };

    const registerMove = (interactionEvent) => {
      moveOverlay(image, overlay, interactionEvent, zoomRatio);
    };

    const endInteraction = (interactionEvent) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (!interactionStart) {
        closeActiveOverlay();
        return;
      }

      const currentPoint = getPointerPosition(interactionEvent, overlay.getBoundingClientRect());
      const deltaX = currentPoint.clientX - interactionStart.clientX;
      const deltaY = currentPoint.clientY - interactionStart.clientY;
      const distance = Math.hypot(deltaX, deltaY);

      if (distance <= TAP_MOVEMENT_THRESHOLD) {
        closeActiveOverlay();
      }

      interactionStart = null;
    };

    overlay.addEventListener('pointermove', (pointerEvent) => {
      if (window.matchMedia('(max-width: 749px)').matches) {
        pointerEvent.preventDefault();
      }
      registerMove(pointerEvent);
    });

    overlay.addEventListener(
      'touchmove',
      (touchEvent) => {
        touchEvent.preventDefault();
        registerMove(touchEvent);
      },
      { passive: false }
    );

    overlay.addEventListener('mouseleave', () => {
      if (window.matchMedia('(min-width: 750px)').matches) {
        closeActiveOverlay();
      }
    });

    overlay.addEventListener('pointerdown', (pointerEvent) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      startInteraction(pointerEvent);
    });

    overlay.addEventListener('touchstart', (touchEvent) => {
      startInteraction(touchEvent);
    });

    overlay.addEventListener('click', () => {
      if (!window.matchMedia('(max-width: 749px)').matches) {
        closeActiveOverlay();
      }
    });

    overlay.addEventListener('pointerup', (pointerEvent) => {
      endInteraction(pointerEvent);
    });

    overlay.addEventListener('touchend', (touchEvent) => {
      endInteraction(touchEvent);
    });
  };
}

function enableZoomOnHover(zoomRatio) {
  const mobileMediaQuery = window.matchMedia('(max-width: 749px)');
  const images = document.querySelectorAll('.image-magnify-hover');

  images.forEach((image) => {
    const opener = image.closest('.product__modal-opener--image');
    const zoomButton = opener?.querySelector('.product__media-toggle.product__media-zoom-hover');
    const zoomIcon = opener?.querySelector('.product__media-icon--hover');

    image.addEventListener('click', (event) => {
      if (mobileMediaQuery.matches) return;
      openMagnify(image, opener, event, zoomRatio);
    });

    if (zoomIcon) {
      zoomIcon.addEventListener(
        'click',
        (event) => {
          if (!mobileMediaQuery.matches) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          event.stopPropagation();

          if (activeOverlay && activeOverlay.image === image) {
            closeActiveOverlay();
            return;
          }

          openMagnify(image, opener, event, zoomRatio);
        },
        true
      );
    }

    if (!zoomButton) return;

    zoomButton.addEventListener(
      'click',
      (event) => {
        if (!mobileMediaQuery.matches) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (activeOverlay && activeOverlay.image === image) {
          closeActiveOverlay();
          return;
        }

        openMagnify(image, opener, event, zoomRatio);
      },
      true
    );
  });
}

enableZoomOnHover(2);
